import { apiKey, baseUrl } from "./variables";
import { readApiErrorMessage } from "../utils/apiErrorUtils";

/**
 * Handle common HTTP response status codes
 * @param {Response} response - Fetch API response object
 * @returns {string|null} - Error code string if error, null if response is ok
 */
const handleResponseErrors = (response) => {
    if (response.status === 400) {
        console.error("Bad Request");
        return "error_400";
    }

    if (response.status === 401) {
        sessionStorage.removeItem("authToken");
        console.error("Unauthorized");
        return "error_401";
    }

    if (response.status === 404) {
        console.error("Not Found");
        return "error_404";
    }

    if (response.status === 500) {
        console.error("Internal Server Error");
        return "error_500";
    }

    if (!response.ok) {
        console.error(response.status);
        return "error_unknown";
    }

    return null; // No error
};

export const sendEmailAndSecurityCode = async (email) => {
    try {
        const storedUUID = sessionStorage.getItem("storedUUID");
        const headers = {
            "X-NLA-API-KEY": apiKey,
            "Content-Type": "application/json",
        };
        const body = JSON.stringify({
            email,
        });

        const requestOptions = {
            method: "POST",
            headers: headers,
            body: body,
            mode: "cors",
            redirect: "follow",
        };

        sessionStorage.setItem("email", email);

        const response = await fetch(
            `${baseUrl}/otp/send/company-setup?uuid=${storedUUID}`,
            requestOptions
        );

        const error = handleResponseErrors(response);
        if (error) {
            return {
                error,
                status: response.status,
                message: await readApiErrorMessage(response),
            };
        }

        const result = await response.json();
        return result;
    } catch (error) {
        return false;
    }
};

export const resendVerificationCode = async (email) => {
    try {
        const storedUUID = sessionStorage.getItem("storedUUID");
        const headers = {
            "X-NLA-API-KEY": apiKey,
            "Content-Type": "application/json",
        };
        const body = JSON.stringify({
            email,
        });
        sessionStorage.setItem("email", email);
        const requestOptions = {
            method: "POST",
            headers: headers,
            body: body,
            mode: "cors",
            redirect: "follow",
        };

        const response = await fetch(
            `${baseUrl}/otp/send/company-setup?uuid=${storedUUID}`,
            requestOptions
        );

        if (!response.ok) {
            console.error(response.status);
            return;
        }

        const result = await response.json();
        return result;
    } catch (error) {
        return error;
    }
};

export const verifySecurityCodeAndEmail = async (code) => {
    const email = sessionStorage.getItem("email");
    const storedUUID = sessionStorage.getItem("storedUUID");
    try {
        const headers = {
            "X-NLA-API-KEY": apiKey,
            "Content-Type": "application/json",
        };
        const raw = JSON.stringify({
            email,
            code,
        });

        const requestOptions = {
            method: "POST",
            headers: headers,
            body: raw,
            redirect: "follow",
            mode: "cors",
        };

        const response = await fetch(
            `${baseUrl}/otp/verify/company-setup?uuid=${storedUUID}`,
            requestOptions
        );

        const error = handleResponseErrors(response);
        if (error) return error;

        const result = await response.json();

        // check if form has been completed
        if (result.isFormComplete) {
            console.error("Form has been completed");
            return "completed";
        }
        return result;
    } catch (error) {
        throw error;
    }
};

// ************** Company ********************//
export const getCompanyDetail = async () => {
    const storedBearerToken = sessionStorage.getItem("authToken");
    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${storedBearerToken}`);

    const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
        mode: "cors",
    };

    const response = await fetch(`${baseUrl}/form/details`, requestOptions);

    if (!response.ok) {
        console.error(response.status);
        return "Error";
    }

    const result = await response.json();
    return result;
};

export const addCompanyDetail = async (apiPayload) => {
    const storedBearerToken = sessionStorage.getItem("authToken");

    try {
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");
        myHeaders.append("Authorization", `Bearer ${storedBearerToken}`);

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: JSON.stringify(apiPayload),
            redirect: "follow",
            mode: "cors",
        };

        const apiUrl = `${baseUrl}/form/details`;
        const response = await fetch(apiUrl, requestOptions);
        const result = await response.json();

        if (!response.ok) {
            console.error(response.status);
            return result;
        }

        return result;
    } catch (error) {
        console.error("Error in addCompanyDetail:", error);
        throw error;
    }
};

// ************** Trading Address ********************//
export const getTradingAddress = async () => {
    const storedBearerToken = sessionStorage.getItem("authToken");

    try {
        const response = await fetch(`${baseUrl}/form/addresses`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${storedBearerToken}`,
            },
        });

        if (!response.ok) {
            console.error(response.status);
            return "Error";
        }

        const data = await response.json();
        return data;
    } catch (error) {
        return error;
    }
};

export const postTradingAddress = async (trading, postal) => {
    const storedBearerToken = sessionStorage.getItem("authToken");

    const newPayload = [
        {
            id: 1,
            type: "trading",
            subPremise: trading.subPremise,
            streetAddress: trading.streetAddress,
            suburb: trading.suburb,
            state: trading.state,
            postcode: trading.postcode,
        },
        {
            id: 2,
            type: "postal",
            subPremise: postal.subPremise,
            streetAddress: postal.streetAddress,
            suburb: postal.suburb,
            state: postal.state,
            postcode: postal.postcode,
        },
    ];

    try {
        const response = await fetch(`${baseUrl}/form/addresses`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${storedBearerToken}`,
            },
            body: JSON.stringify(newPayload),
        });

        const data = await response.json();

        if (!response.ok) {
            console.error(response.status);
            return data;
        }
        return data;
    } catch (error) {
        throw error;
    }
};

// ************** Contact ********************//
export const getContactDetail = async () => {
    const storedBearerToken = sessionStorage.getItem("authToken");

    try {
        const response = await fetch(`${baseUrl}/form/contacts`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${storedBearerToken}`,
            },
        });

        if (!response.ok) {
            console.error(response.status);
            return "Error";
        }

        const data = await response.json();
        return data;
    } catch (error) {
        return error;
    }
};

export const postContactDetail = async (newPayload) => {
    const storedBearerToken = sessionStorage.getItem("authToken");

    try {
        const response = await fetch(`${baseUrl}/form/contacts`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${storedBearerToken}`,
            },
            body: JSON.stringify(newPayload),
        });
        const data = await response.json();

        if (!response.ok) {
            console.error(response.status);
            return data;
        }

        return data;
    } catch (error) {
        throw error;
    }
};

// ************** Terms and Condition ********************//
export const getTermsAndCondition = async () => {
    const storedBearerToken = sessionStorage.getItem("authToken");

    try {
        const response = await fetch(`${baseUrl}/form/terms`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${storedBearerToken}`,
            },
        });

        if (!response.ok) {
            console.error(response.status);
            return "Error";
        }

        const data = await response.json();
        return data;
    } catch (error) {
        return error;
    }
};

export const postTermsAndCondition = async (newPayload) => {
    const storedBearerToken = sessionStorage.getItem("authToken");

    try {
        const response = await fetch(`${baseUrl}/form/terms`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${storedBearerToken}`,
            },
            body: JSON.stringify(newPayload),
        });

        if (!response.ok) {
            console.error(response.status);
            return "Error";
        }

        const data = await response.json();
        return data;
    } catch (error) {
        throw error;
    }
};

// ************** Payments ********************//
export const getPayments = async () => {
    const storedBearerToken = sessionStorage.getItem("authToken");

    try {
        const response = await fetch(`${baseUrl}/form/account`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${storedBearerToken}`,
            },
        });

        if (!response.ok) {
            console.error(response.status);
            return "Error";
        }

        const data = await response.json();
        return data;
    } catch (error) {
        return error;
    }
};

export const postPayments = async (newPayload) => {
    const storedBearerToken = sessionStorage.getItem("authToken");

    try {
        const response = await fetch(`${baseUrl}/form/account`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${storedBearerToken}`,
            },
            body: JSON.stringify(newPayload),
        });

        const data = await response.json();

        if (!response.ok) {
            console.error(response.status);
            return data;
        }
        return data;
    } catch (error) {
        throw error;
    }
};

// ************** Associations - Dropdowns and Terms Data ********************//
export const getAssociations = async () => {
    const storedBearerToken = sessionStorage.getItem("authToken");

    try {
        const response = await fetch(`${baseUrl}/associations`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${storedBearerToken}`,
            },
        });

        if (!response.ok) {
            console.error(response.status);
            return "Error";
        }

        const data = await response.json();
        return data;
    } catch (error) {
        return error;
    }
};

// ************** Review ********************//
export const getFormReview = async () => {
    const storedBearerToken = sessionStorage.getItem("authToken");

    try {
        const response = await fetch(`${baseUrl}/form/review`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${storedBearerToken}`,
            },
        });

        if (!response.ok) {
            console.error(response.status);
            return "Error";
        }

        const data = await response.json();
        return data;
    } catch (error) {
        return error;
    }
};

export const postFormReview = async () => {
    const storedBearerToken = sessionStorage.getItem("authToken");

    try {
        const response = await fetch(`${baseUrl}/form/review`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${storedBearerToken}`,
            },
            body: JSON.stringify({}),
        });

        const result = await response.json();

        if (!response.ok) {
            console.error(response.status);
            return result;
        }

        return result;
    } catch (error) {
        throw error;
    }
};

// ************** Upload File ********************//
export const requestPresignedURL = async (imageName, imageType, type) => {
    const storedBearerToken = sessionStorage.getItem("authToken");
    const apiPayload = {
        FileName: imageName,
        ContentType: imageType,
        FileSize: 38795,
        Expiry: 5,
    };

    try {
        const response = await fetch(
            `${baseUrl}/request-presigned?uuid=${sessionStorage.getItem("storedUUID")}&type=${type}`,
            {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${storedBearerToken}`,
                },
                body: JSON.stringify(apiPayload),
            }
        );

        if (!response.ok) {
            console.error(response.status);
            return;
        }

        const data = await response.json();
        return data;
    } catch (error) {
        throw error;
    }
};

export const uploadToS3 = async (presignedData, file) => {
    try {
        // Create FormData object
        const formData = new FormData();

        // Append all fields from presignedData.fields to FormData
        // The order matters: all fields must come before the file
        Object.keys(presignedData.fields).forEach((key) => {
            formData.append(key, presignedData.fields[key]);
        });

        // Append the file last (this is important for S3)
        formData.append("file", file);

        // POST FormData
        const response = await fetch(presignedData.uploadUrl, {
            method: "POST",
            body: formData,
        });

        if (!response.ok) {
            console.error(
                `S3 Upload failed: ${response.status} ${response.statusText}`
            );
            const errorText = await response.text();
            console.error("Error details:", errorText);
            throw new Error(`Upload failed: ${response.statusText}`);
        }

        return {
            success: true,
            status: response.status,
            fileUrl: presignedData.path, // Return the file URL from presignedData
        };
    } catch (error) {
        console.error("S3 Upload error:", error);
        throw error;
    }
};

// ************** Assets ********************//
export const getAssets = async () => {
    const storedBearerToken = sessionStorage.getItem("authToken");
    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${storedBearerToken}`);

    const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
        mode: "cors",
    };

    const response = await fetch(`${baseUrl}/form/assets`, requestOptions);

    if (!response.ok) {
        console.error(response.status);
        return "Error";
    }

    const result = await response.json();
    return result;
};

export const postAssets = async (fileName, fileUrl, type) => {
    const storedBearerToken = sessionStorage.getItem("authToken");

    const newPayload = {
        filename: fileName,
        path: fileUrl,
        type: type,
    };

    try {
        const response = await fetch(`${baseUrl}/form/assets`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${storedBearerToken}`,
            },
            body: JSON.stringify(newPayload),
        });

        if (!response.ok) {
            console.error(response.status);
            return;
        }

        const data = await response.json();
        return data;
    } catch (error) {
        throw error;
    }
};
